import { useEffect, useState } from 'react';
import { DrawerExercise } from './DrawerExercise';
import { filter, FilterProps } from '../../utils/exercise-filter';
import SearchIcon from '@mui/icons-material/Search';
import { Drawer, IconButton, InputAdornment, MenuItem, Paper, Select, Stack, TextField, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ExerciseDTO } from '../../generated';
import { filterBodyzones } from '@/utils/bodyzone-filter';
import { useAllExercisesQuery, useBodyZoneListQuery } from '@/hooks/query-hooks';
import ExerciseCategoryAutocomplete from '../exercise-categories/ExerciseCategoryAutocomplete';
import EquipmentAutocomplete from '../equipment/EquipmentAutocomplete';
import { Close, Search, SearchOff } from '@mui/icons-material';

type SelectExercisesDrawerProps = {
  show: boolean;
  setShow: (show: boolean) => void;
  exerciseSelected: (exercise: ExerciseDTO) => void;
};

export const SelectExercisesDrawer = (props: SelectExercisesDrawerProps) => {
  const { exerciseSelected, setShow, show } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [filterProps, setFilterProps] = useState<FilterProps>();
  const [filteredExercises, setFilteredExercises] = useState<Array<ExerciseDTO>>();
  const { exercises: unfilteredEx, error } = useAllExercisesQuery(show);
  const [magnifyingGlass, setMagnifyingGlass] = useState<boolean>(true);
  const theme = useTheme();
  const { bodyZoneList } = useBodyZoneListQuery();

  useEffect(() => {
    if (error) enqueueSnackbar(`Failed to load Exercises ${error}`, { variant: 'error' });
  }, [error]);

  useEffect(() => {
    const exercises = unfilteredEx;

    if (filterProps && exercises) setFilteredExercises(filter(exercises ?? [], filterProps));
    else setFilteredExercises(exercises);
  }, [filterProps, unfilteredEx]);

  return (
    <>
      <Drawer anchor="right" open={show} onClose={() => setShow(false)} sx={{ zIndex: theme.zIndex.modal + 1 }}>
        <Stack sx={{ alignItems: 'center', width: { xs: '100vw', sm: 500 }, maxWidth: '100vw' }}>
          <Paper elevation={20} sx={{ position: 'sticky', top: 0, pb: 3, width: '100%', zIndex: 1 }}>
            <Stack direction={'row'} sx={{ mt: 1, mx: 2, mb: 2, background: theme.palette.grey[700], borderRadius: 2 }}>
              <IconButton
                sx={{ mx: 1 }}
                onClick={async () => {
                  props.setShow(false);
                }}
              >
                <Close />
              </IconButton>
              <Stack direction={'row'} sx={{ mr: 2, ml: 'auto' }}>
                <IconButton onClick={() => setMagnifyingGlass(!magnifyingGlass)}>{magnifyingGlass ? <Search /> : <SearchOff />}</IconButton>
              </Stack>
            </Stack>
            {magnifyingGlass && (
              <Stack direction={'column'} sx={{ gap: 3, alignContent: 'dl', pt: 3, mx: 2 }}>
                <Stack style={{ flexDirection: 'row', gap: 20 }}>
                  <TextField
                    value={filterProps?.name}
                    sx={{}}
                    label="Name"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="start">
                            <Close sx={{ cursor: 'pointer' }} onClick={() => setFilterProps({ ...filterProps, name: '' })} />
                          </InputAdornment>
                        ),
                      },
                    }}
                    onChange={(e: any) => {
                      setFilterProps({ ...filterProps, name: e.target.value });
                    }}
                  />
                  <EquipmentAutocomplete
                    selectedEquipment={filterProps?.equipmentIds?.map((id) => ({ equipmentId: id, optional: false })) ?? []}
                    setSelectedEquipment={(eq) => setFilterProps({ ...filterProps, equipmentIds: eq.map((e) => e.equipmentId) })}
                  />
                </Stack>
                <Stack style={{ flexDirection: 'row', gap: 20 }}>
                  <ExerciseCategoryAutocomplete
                    selectedCategoryIds={filterProps?.categoryIds ?? []}
                    setSelectedCategoryIds={(ids) => setFilterProps({ ...filterProps, categoryIds: ids })}
                  />
                  <Select
                    MenuProps={{ sx: { zIndex: theme.zIndex.modal + 2 } }}
                    value={filterProps?.bodyZoneId !== '' && filterProps?.bodyZoneId ? filterProps?.bodyZoneId : 'any'}
                    onChange={(e) => {
                      if (e.target.value !== 'any') setFilterProps({ ...filterProps, bodyZoneId: e.target.value as any });
                      else setFilterProps({ ...filterProps, bodyZoneId: '' });
                    }}
                  >
                    <MenuItem key={'any'} value={'any'}>
                      Any Zone
                    </MenuItem>

                    {filterBodyzones(bodyZoneList ?? []).map((bz) => (
                      <MenuItem key={bz.id} value={bz.id}>
                        {bz.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              </Stack>
            )}
          </Paper>
          <Stack sx={{ width: '100%' }}>
            {filteredExercises?.map((e) => (
              <DrawerExercise onClick={() => exerciseSelected(e)} key={e.id} exercise={e} />
            ))}
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
};
